/**
 * FILE: src/blueprint/screens/Module/ModuleRunnerScreen.jsx
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUserErrorMessage } from '../../../utils/errorMessages.js';
import {
  fetchClientConfig,
  fetchModuleDoc,
  getServerBaseUrlFromConfig,
  postResetModule,
} from '../../api/clientApi.js';
import InsightsPanel from '../../components/insights/InsightsPanel.jsx';
import StepNav from '../../components/navigation/StepNav.jsx';
import ProgressPopover from '../../components/progress/ProgressPopover.jsx';
import ContentRenderer, {
  getResolvedStepType,
} from '../../components/renderers/ContentRenderer.jsx';
import useCoachInteractionFlow from '../../hooks/useCoachInteractionFlow.js';
import useProgressiveInsights from '../../hooks/useProgressiveInsights.js';
import applyInitialRunState from '../../state/applyInitialRunState.js';
import { ACTIONS } from '../../state/blueprintReducer.js';
import loadInitialRunState from '../../state/loadInitialRunState.js';
import {
  selectActiveModule,
  selectActiveSteps,
  selectActiveTrackId,
} from '../../state/selectors.js';
import { useBlueprintContentNav } from '../../state/useBlueprintContentNav.js';
import { useBlueprintRouter } from '../../state/useBlueprintRouter.js';
import { useBlueprintState } from '../../state/useBlueprintState.js';
import { buildProgressiveViewModel } from '../../utils/progressiveViewModel.js';

const REPORT_SUBJECT_STEP_ID = '__report_subject_name';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function makeAnswerKey(packId, moduleId, trackId, stepId) {
  const p = String(packId || '').trim();
  const m = String(moduleId || '').trim();
  const t = String(trackId || '').trim();
  const s = String(stepId || '').trim();

  if (!p || !m || !t || !s) {
    return s;
  }

  return `${p}::${m}::${t}::${s}`;
}

function getAnswerForStep(state, packId, moduleId, trackId, stepId) {
  const answers = state?.answers && typeof state.answers === 'object' ? state.answers : {};
  const key = makeAnswerKey(packId, moduleId, trackId, stepId);

  return answers[String(key || '')];
}

function getStepRenderType(step) {
  const kind = String(step?.kind || '').trim();
  const inputType = String(step?.inputType || '').trim();
  const legacyType = String(step?.type || '').trim();

  if (kind === 'info') return 'info';
  if (kind === 'report') return 'report';

  return inputType || legacyType || kind;
}

function isStepValid(step, state, packId, moduleId, trackId) {
  if (!step) {
    return false;
  }

  const type = getStepRenderType(step);

  if (type === 'info' || type === 'video' || type === 'report') {
    console.log('ModuleRunnerScreen.isValid is alway true for ' + type);
    return true;
  }

  const answer = getAnswerForStep(state, packId, moduleId, trackId, step.id);

  console.log('[isStepValid]', {
    stepId: step?.id,
    type,
    min: step?.min,
    max: step?.max,
    answer,
    answerType: typeof answer,
    answerIsArray: Array.isArray(answer),
  });

  if (type === 'text' || type === 'textarea') {
    const value = typeof answer === 'string' ? answer.trim() : '';
    return value !== '';
  }

  if (type === 'single') {
    const value = typeof answer === 'string' ? answer.trim() : '';
    return value !== '';
  }

  if (type === 'select_cards') {
    const value = asObject(answer);
    const choices = asArray(step.choices);

    const choiceIds = new Set(
      choices.map((choice) => String(choice?.id || '').trim()).filter(Boolean)
    );

    const validAnswers = new Set(['yes', 'maybe', 'no']);

    const validEntries = Object.entries(value).filter(([choiceId, choiceAnswer]) => {
      return choiceIds.has(String(choiceId)) && validAnswers.has(String(choiceAnswer).trim());
    });

    const count = validEntries.length;
    const min = Number(step.min ?? choiceIds.size) || choiceIds.size;
    const max = Number(step.max ?? choiceIds.size) || choiceIds.size;

    console.log('[select_cards validation]', {
      choicesCount: choiceIds.size,
      entries: Object.entries(value),
      validEntries,
      count,
      min,
      max,
      isValid: count >= min && (max <= 0 || count <= max),
    });

    if (count < min) return false;
    if (max > 0 && count > max) return false;

    return true;
  }

  if (type === 'multi') {
    const values = asArray(answer).map((item) => String(item));
    const count = values.length;
    const min = Number(step.min ?? 1) || 1;
    const max = Number(step.max ?? 0) || 0;

    if (count < min) return false;
    if (max > 0 && count > max) return false;

    return count > 0;
  }

  if (type === 'rank') {
    return asArray(answer).length > 0;
  }

  if (type === 'matrix_single') {
    const value = asObject(answer);
    const entries = Object.entries(value);

    const rows = asArray(step.rows);
    const columns = asArray(step.columns);

    const rowIds = new Set(rows.map((row) => String(row?.id || '').trim()).filter(Boolean));
    const columnIds = new Set(
      columns.map((column) => String(column?.id || '').trim()).filter(Boolean)
    );

    const min = Number(step.min ?? 1) || 1;
    const max = Number(step.max ?? 0) || 0;

    const validEntries = entries.filter(([rowId, columnId]) => {
      return rowIds.has(String(rowId)) && columnIds.has(String(columnId));
    });

    const count = validEntries.length;

    if (count < min) return false;
    if (max > 0 && count > max) return false;

    return count === entries.length;
  }

  return false;
}

export default function ModuleRunnerScreen() {
  const { state, dispatch, pilotConsentAccepted } = useBlueprintState();
  const { goToOverview } = useBlueprintRouter();

  const moduleLoadAbortRef = useRef(null);
  const importInputRef = useRef(null);

  const [isProgressPopupOpen, setIsProgressPopupOpen] = useState(false);
  const [isRailOpen, setIsRailOpen] = useState(true);
  const [hasHydratedRunState, setHasHydratedRunState] = useState(false);
  const [reportSubjectName, setReportSubjectName] = useState('');

  const packId = String(state?.packId || '').trim();
  const module = selectActiveModule(state);
  const activeTrackId = selectActiveTrackId(state);
  const trackId = activeTrackId || module?.default_track_id || 'quick';
  const steps = selectActiveSteps(state);
  const stepsList = asArray(steps);

  console.log('[ModuleRunner] activeModuleDoc full', state?.activeModuleDoc);
  console.log('[ModuleRunner] activeModuleDoc keys', Object.keys(state?.activeModuleDoc || {}));
  console.log('[ModuleRunner] activeModuleDoc.steps', state?.activeModuleDoc?.steps);

  const reportSubjectAnswerKey = useMemo(() => {
    return makeAnswerKey(packId, module?.id, trackId, REPORT_SUBJECT_STEP_ID);
  }, [packId, module?.id, trackId]);

  const savedReportSubjectName = useMemo(() => {
    const answers = state?.answers && typeof state.answers === 'object' ? state.answers : {};
    const value = answers[reportSubjectAnswerKey];

    return typeof value === 'string' ? value : '';
  }, [state?.answers, reportSubjectAnswerKey]);

  useEffect(() => {
    setReportSubjectName(savedReportSubjectName);
  }, [savedReportSubjectName]);

  const selectedCoachId = asString(state?.selectedCoachId).trim() || 'system';
  const selectedTypingMode = asString(state?.selectedTypingMode).trim() || 'fast';

  const serverBaseUrl = useMemo(() => {
    return getServerBaseUrlFromConfig(state?.config);
  }, [state?.config]);

  const {
    currentItem,
    stepIndex,
    stepCount,
    canPrev,
    canNext,
    isLast,
    prev,
    next,
    goTo,
    first,
    last,
  } = useBlueprintContentNav();

  const activeStep = stepsList?.[stepIndex] ?? currentItem ?? null;
  const rawActiveStepId = String(activeStep?.id ?? currentItem?.id ?? '').trim();
  const activeStepId =
    module?.id && rawActiveStepId
      ? `${String(module.id).trim()}.${rawActiveStepId}`
      : rawActiveStepId;

  const isCurrentValid = useMemo(() => {
    return isStepValid(currentItem, state, packId, module?.id, trackId);
  }, [currentItem, state, packId, module?.id, trackId]);

  const canNextValidated = Boolean(canNext) && Boolean(isCurrentValid);

  const runProgressiveInsights = useProgressiveInsights({
    dispatch,
    serverBaseUrl,
    answers: state?.answers,
    steps: stepsList,
    packId,
    moduleId: module?.id,
    trackId,
    currentStepId: activeStepId,
    assistantId: selectedCoachId,
    typingMode: selectedTypingMode,
    enabled:
      pilotConsentAccepted && Boolean(module?.id) && stepsList.length > 0 && hasHydratedRunState,
  });

  const progressiveRunnerRef = useRef(null);
  const lastProgressiveStepKeyRef = useRef('');

  useEffect(() => {
    progressiveRunnerRef.current = runProgressiveInsights;
  }, [runProgressiveInsights]);

  useEffect(() => {
    if (!pilotConsentAccepted) return;
    if (!hasHydratedRunState) return;
    if (!module?.id) return;
    if (!trackId) return;
    if (!activeStepId) return;
    if (!stepsList.length) return;

    const key = `${packId}::${module.id}::${trackId}::${activeStepId}`;

    if (lastProgressiveStepKeyRef.current === key) {
      return;
    }

    lastProgressiveStepKeyRef.current = key;

    void progressiveRunnerRef.current?.();
  }, [
    pilotConsentAccepted,
    hasHydratedRunState,
    packId,
    module?.id,
    trackId,
    activeStepId,
    stepsList.length,
  ]);

  const nextValidated = useCallback(() => {
    if (!canNextValidated) return;

    next();
  }, [canNextValidated, next]);

  const answersVersion = useMemo(() => {
    const answers = state?.answers && typeof state.answers === 'object' ? state.answers : {};
    return Object.keys(answers).length;
  }, [state?.answers]);

  useEffect(() => {
    if (!pilotConsentAccepted) {
      moduleLoadAbortRef.current?.abort?.();

      dispatch({
        type: ACTIONS.SET_LOADING,
        payload: { isLoading: false },
      });

      setHasHydratedRunState(false);
      return;
    }

    moduleLoadAbortRef.current?.abort?.();

    const controller = new AbortController();
    moduleLoadAbortRef.current = controller;

    (async () => {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: { error: '' },
      });

      dispatch({
        type: ACTIONS.SET_LOADING,
        payload: { isLoading: true },
      });

      setHasHydratedRunState(false);

      try {
        const config = await fetchClientConfig({ signal: controller.signal });

        dispatch({
          type: ACTIONS.SET_CONFIG,
          payload: { config },
        });

        const resolvedServerBaseUrl = getServerBaseUrlFromConfig(config);
        const moduleId = String(module?.id || '').trim();

        if (!resolvedServerBaseUrl || !moduleId) {
          throw new Error('Missing server_base_url or module_id.');
        }

        const moduleDocResponse = await fetchModuleDoc({
          serverBaseUrl: resolvedServerBaseUrl,
          packId,
          moduleId,
          trackId,
          signal: controller.signal,
        });

        const moduleDoc = moduleDocResponse?.data || moduleDocResponse;

        console.log('[ModuleRunner] moduleDoc', moduleDoc);
        console.log('[ModuleRunner] moduleDoc.steps', moduleDoc?.steps);

        dispatch({
          type: ACTIONS.SET_ACTIVE_MODULE_DOC,
          payload: { moduleDoc },
        });

        const loadedRunState = await loadInitialRunState({
          packId,
          moduleId,
          track: trackId,
        });

        applyInitialRunState({
          initialState: loadedRunState,
          dispatch,
          packId,
          moduleId,
          trackId,
          activeModuleDoc: moduleDoc,
        });

        setHasHydratedRunState(true);
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error('[Savora] Failed to load module runner state.', error);

        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: { error: getUserErrorMessage(error) || 'Failed to load module.' },
        });

        setHasHydratedRunState(true);
      } finally {
        dispatch({
          type: ACTIONS.SET_LOADING,
          payload: { isLoading: false },
        });
      }
    })();

    return () => controller.abort();
  }, [dispatch, packId, module?.id, trackId, pilotConsentAccepted]);

  const stepLabel = stepCount > 0 ? `Step ${stepIndex + 1} / ${stepCount}` : 'Step - / -';

  const rawRailEntries = Array.isArray(state?.insights?.railEntries)
    ? state.insights.railEntries
    : Array.isArray(state?.insights?.rail)
      ? state.insights.rail
      : [];

  const rawStepEntries = Array.isArray(state?.insights?.stepEntries)
    ? state.insights.stepEntries
    : Array.isArray(state?.insights?.steps)
      ? state.insights.steps
      : [];

  const rawReportEntries = Array.isArray(state?.insights?.reportEntries)
    ? state.insights.reportEntries
    : Array.isArray(state?.insights?.report_entries)
      ? state.insights.report_entries
      : [];

  const progressiveView = useMemo(() => {
    return buildProgressiveViewModel({
      rail: rawRailEntries,
      steps: rawStepEntries,
      reportEntries: rawReportEntries,
    });
  }, [rawRailEntries, rawStepEntries, rawReportEntries]);

  const baseCoachEntries = asArray(progressiveView?.railAssistants).filter((entry) => {
    return String(entry?.delivery || entry?.raw?.delivery || '').trim() !== 'interaction';
  });

  const coachInteraction = useCoachInteractionFlow({
    dispatch,
    activeStepId,
    packId,
    moduleId: module?.id,
    trackId,
    steps: stepsList,
    answers: state?.answers,
    selectedCoachId,
    selectedTypingMode,
    baseEntries: baseCoachEntries,
  });

  const reportEntriesById = useMemo(() => {
    return progressiveView?.reportsById && typeof progressiveView.reportsById === 'object'
      ? progressiveView.reportsById
      : {};
  }, [progressiveView]);

  const currentRenderableStep = useMemo(() => {
    return currentItem && typeof currentItem === 'object' ? currentItem : null;
  }, [currentItem]);

  const currentReportId = useMemo(() => {
    return asString(currentRenderableStep?.reportId).trim();
  }, [currentRenderableStep]);

  const currentReportEntries = useMemo(() => {
    if (!currentReportId) {
      return [];
    }

    return asArray(progressiveView?.steps).filter((entry) => {
      const reportId = asString(entry?.reportId).trim();
      const sourceId = asString(entry?.sourceId).trim();
      const entryId = asString(entry?.id).trim();
      const reportType = asString(entry?.reportType).trim();

      return (
        reportId === currentReportId ||
        sourceId === currentReportId ||
        entryId === currentReportId ||
        reportType === currentReportId
      );
    });
  }, [currentReportId, progressiveView]);

  const currentStepType = useMemo(() => {
    return getStepRenderType(currentRenderableStep);
  }, [currentRenderableStep]);

  const handleReportSubjectNameChange = useCallback(
    (event) => {
      const value = String(event?.target?.value || '');
      const answers = state?.answers && typeof state.answers === 'object' ? state.answers : {};

      dispatch({
        type: ACTIONS.SET_ANSWERS,
        payload: {
          answers: {
            ...answers,
            [reportSubjectAnswerKey]: value,
          },
        },
      });
    },
    [dispatch, reportSubjectAnswerKey, state?.answers]
  );

  const handleResetModule = useCallback(async () => {
    const ok = window.confirm(
      'Reset this module? This will remove saved answers, progress, and generated results for this module.'
    );

    if (!ok) {
      return;
    }

    const moduleId = String(module?.id || '').trim();
    const activeTrack = String(trackId || 'quick').trim();

    if (!packId || !moduleId || !activeTrack) {
      return;
    }

    dispatch({ type: ACTIONS.SET_ERROR, payload: { error: '' } });
    dispatch({ type: ACTIONS.SET_LOADING, payload: { isLoading: true } });

    try {
      dispatch({
        type: ACTIONS.SET_ANSWERS,
        payload: { answers: {} },
      });

      await postResetModule({
        payload: {
          pack_id: packId,
          module_id: moduleId,
          track_id: activeTrack,
        },
      });

      Object.keys(sessionStorage)
        .filter(
          (key) =>
            key.startsWith('digivibe_coach_typed__') ||
            key.includes(packId) ||
            key.includes(moduleId) ||
            key.includes(activeTrack)
        )
        .forEach((key) => sessionStorage.removeItem(key));

      window.location.replace(`/run/?pack=${encodeURIComponent(packId)}`);
    } catch (e) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: { error: getUserErrorMessage(e) },
      });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: { isLoading: false } });
    }
  }, [dispatch, packId, module?.id, trackId]);

  const handleExportAnswers = useCallback(() => {
    const moduleId = String(module?.id || '').trim();
    const activeTrack = String(trackId || 'quick').trim();

    const exportData = {
      schemaVersion: '1.0.0',
      type: 'savora.answers',
      exportedAt: new Date().toISOString(),
      packId,
      moduleId,
      trackId: activeTrack,
      answers: state?.answers && typeof state.answers === 'object' ? state.answers : {},
    };

    const fileNameParts = ['savora-answers', packId, moduleId, activeTrack].filter(Boolean);
    const fileName = `${fileNameParts.join('-')}.json`;

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  }, [packId, module?.id, trackId, state?.answers]);

  const handleImportAnswers = useCallback(() => {
    importInputRef.current?.click();
  }, []);

  const handleImportAnswersFile = useCallback(
    async (event) => {
      const file = event?.target?.files?.[0] || null;

      if (!file) {
        return;
      }

      try {
        const text = await file.text();
        const parsed = JSON.parse(text);

        const importedAnswers =
          parsed?.answers && typeof parsed.answers === 'object' && !Array.isArray(parsed.answers)
            ? parsed.answers
            : null;

        if (!importedAnswers) {
          throw new Error('The selected file does not contain valid Savora answers.');
        }

        dispatch({
          type: ACTIONS.SET_ANSWERS,
          payload: { answers: importedAnswers },
        });

        await runProgressiveInsights({
          answersOverride: importedAnswers,
        });
      } catch (error) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            error: error?.message || 'Failed to import answers.',
          },
        });
      } finally {
        event.target.value = '';
      }
    },
    [dispatch, runProgressiveInsights]
  );

  const insightsMeta = asObject(state?.insights?.meta);
  const serverProgress = insightsMeta.progress ?? state?.insights?.progress ?? null;
  const serverConfidence = insightsMeta.confidence ?? state?.insights?.confidence ?? null;
  const milestone = insightsMeta.milestone ?? state?.insights?.milestone ?? null;
  const milestoneBadge = insightsMeta.milestoneBadge ?? state?.insights?.milestoneBadge ?? '';
  const milestoneId = typeof milestone?.id === 'string' ? milestone.id : '';

  const progressRatio = Number(serverProgress?.ratio ?? 0);
  const assistantEntriesForRail = coachInteraction.displayEntries;

  return (
    <div className="dv-br-shell">
      <header className="dv-br-topbar">
        <div className="dv-br-topbar__left">
          <div>
            <button type="button" onClick={goToOverview} aria-label="Back">
              ← Overview
            </button>

            <button type="button" className="dv-br-danger" onClick={handleResetModule}>
              Reset module
            </button>

            <button
              type="button"
              className="dv-br-moduleRunner__iconButton"
              title="Export answers"
              aria-label="Export answers"
              onClick={handleExportAnswers}
            >
              Export
            </button>

            <button
              type="button"
              className="dv-br-moduleRunner__iconButton"
              title="Import answers"
              aria-label="Import answers"
              onClick={handleImportAnswers}
            >
              Import
            </button>
          </div>
        </div>

        <div className="dv-br-topbar__title">
          {module?.title ?? 'Module'} - {trackId}
        </div>

        <div className="dv-br-topbar__right">
          <ProgressPopover
            serverProgress={serverProgress}
            serverConfidence={serverConfidence}
            progressPopover={state?.insights?.progressPopover}
            isOpen={isProgressPopupOpen}
            onToggle={() => setIsProgressPopupOpen((current) => !current)}
            onClose={() => setIsProgressPopupOpen(false)}
          />
        </div>
      </header>

      <main className="dv-br-main">
        <div
          className={`dv-br-module ${
            isRailOpen ? 'dv-br-module--rail-open' : 'dv-br-module--rail-closed'
          }`}
        >
          <section className="dv-br-module__main" aria-label="Module content">
            <div className="dv-br-stepHeader">
              <h2 className="dv-br-stepHeader__title">{module?.title ?? 'Module'}</h2>
              <p className="dv-br-stepHeader__meta">{stepLabel}</p>
            </div>

            <StepNav
              moduleTitle={module?.id ? String(module.id) : ''}
              steps={stepsList}
              currentIndex={stepIndex}
              onGoFirst={first}
              onGoLast={last}
              onGoPrev={prev}
              onGoNext={nextValidated}
              onGoTo={goTo}
              isStepValid={(step) => isStepValid(step, state, packId, module?.id, trackId)}
              answersVersion={answersVersion}
            />

            <p>{getResolvedStepType(currentRenderableStep)}</p>
            {getResolvedStepType(currentRenderableStep) === 'report' && (
              <div className="dv-br-own-label">
                <label htmlFor="savora-report-subject-name">
                  [OPTIONAL] text to your PDF reports{' '}
                </label>
                <input
                  id="savora-report-subject-name"
                  type="text"
                  value={reportSubjectName}
                  onChange={handleReportSubjectNameChange}
                  placeholder=""
                />
              </div>
            )}

            <ContentRenderer
              item={currentRenderableStep}
              packId={packId}
              reportSubjectName={reportSubjectName}
              reportEntriesById={reportEntriesById}
              reportEntries={currentStepType === 'report' ? currentReportEntries : []}
              pilotIdentity={
                state?.insights?.pilotIdentity ?? state?.insights?.meta?.pilotIdentity ?? null
              }
            />

            <nav className="dv-br-nav" aria-label="Step navigation">
              <div className="dv-br-nav__left">
                <button
                  type="button"
                  className="dv-br-btn dv-br-btn--secondary"
                  onClick={prev}
                  disabled={!canPrev}
                >
                  Previous
                </button>
              </div>

              <div className="dv-br-nav__right">
                {!isLast && (
                  <button
                    type="button"
                    className={`dv-br-btn dv-br-btn--primary ${isLast ? 'last-step' : ''}`}
                    onClick={nextValidated}
                    disabled={!canNextValidated}
                  >
                    Next
                  </button>
                )}
                {isLast && (
                  <button
                    type="button"
                    className={`dv-br-btn dv-br-btn--primary last-step`}
                    onClick={goToOverview}
                    disabled={false}
                  >
                    ← Back to Overview
                  </button>
                )}
              </div>
            </nav>
          </section>

          <aside className="dv-br-module__side" aria-label="Module side panel">
            {isRailOpen ? (
              <>
                <button
                  type="button"
                  className="dv-br-module__sideClose"
                  onClick={() => setIsRailOpen(false)}
                  aria-expanded={true}
                  aria-label="Close help panel"
                >
                  ×
                </button>

                <div className="dv-br-module__sideInner">
                  <div className="dv-br-module__sideHeader">
                    <h3 className="dv-br-module__sideTitle">
                      Help
                      {milestoneBadge ? (
                        <span
                          className={`dv-br-badge dv-br-badge--${String(
                            milestoneBadge
                          ).toLowerCase()}`}
                          title={milestoneId ? `Milestone: ${milestoneId}` : 'Milestone updated'}
                          style={{ marginLeft: 10 }}
                        >
                          {String(milestoneBadge)}
                        </span>
                      ) : null}
                    </h3>
                  </div>

                  <InsightsPanel
                    insightEntries={asArray(progressiveView?.railContent)}
                    assistantEntries={assistantEntriesForRail}
                    enabled={
                      Boolean(state?.insights?.isLoading) ||
                      asArray(progressiveView?.rail).length > 0 ||
                      assistantEntriesForRail.length > 0 ||
                      Number(serverProgress?.answered ?? 0) > 0
                    }
                    progressRatio={progressRatio}
                    selectedCoachId={selectedCoachId}
                    selectedTypingMode={selectedTypingMode}
                    currentStepId={activeStepId}
                    onCoachChoiceSelect={coachInteraction.handleChoiceSelect}
                    onCoachFeedback={coachInteraction.handleFeedback}
                    onRestartCoachChoices={coachInteraction.handleRestart}
                    closedCoachFeedbackIds={coachInteraction.closedFeedbackIds}
                    hiddenCoachChoiceBlockIds={coachInteraction.hiddenChoiceBlockIds}
                    isCoachInteractionLoading={coachInteraction.isLoading}
                    assistantTitle={
                      progressiveView?.assistant?.persona?.name ||
                      state?.insights?.assistant?.active?.persona?.name ||
                      state?.insights?.assistant?.active?.label ||
                      state?.insights?.assistant?.active?.title ||
                      state?.insights?.assistant?.active?.id ||
                      'Coach'
                    }
                    assistantPersona={
                      progressiveView?.assistant?.persona ||
                      state?.insights?.assistant?.active?.persona ||
                      null
                    }
                  />

                  {state?.error ? (
                    <p className="dv-br-module__sideText">⚠ {String(state.error)}</p>
                  ) : null}
                </div>
              </>
            ) : (
              <button
                type="button"
                className="dv-br-module__sideToggle dv-br-module__sideToggle--collapsed"
                onClick={() => setIsRailOpen(true)}
                aria-expanded={false}
                aria-label="Open help panel"
              >
                Help
              </button>
            )}
          </aside>
        </div>
      </main>

      <footer>
        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={handleImportAnswersFile}
        />
      </footer>
    </div>
  );
}
